"use client"

import { useState, useEffect, useMemo } from "react"
import {
  Container,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputAdornment,
  TablePagination,
  TableSortLabel,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box, // Import Box from Material-UI
  Snackbar,
} from "@material-ui/core"
import Alert from "@material-ui/lab/Alert"
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
} from "@material-ui/icons"
// Importamos la nueva biblioteca de QR
import QRCode from "qrcode.react"
// Mantenemos la biblioteca anterior para compatibilidad
import ReactQRCode from "react-qr-code"
import firebase from "../firebase"
import Navbar from "./Navbar"
import { getNextId, isCounterInitialized, initializeCounterAndAssignIds } from "../utils/counter"
import { resolveVolumeSize, VOLUME_SIZE_OPTIONS, isValidForCodesImport } from "../utils/volumeDetector"

// URL base constante para los códigos QR
const BASE_URL = "https://3xp.lat"

// URL base para la API de MagicBox
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL // Reemplazar con la URL real de la API

// API Key para MagicBox desde variables de entorno
const MAGICBOX_API_KEY = process.env.REACT_APP_MAGICBOX_API_KEY

// Opciones de logos disponibles
const LOGO_OPTIONS = [
  { value: "logo_bermuda.png", label: "Logo Bermuda" },
  { value: "logo_tp.png", label: "Logo TP" },
]

function Dashboard() {
  const [url, setUrl] = useState("")
  const [title, setTitle] = useState("")
  const [qrCodes, setQrCodes] = useState([])
  const [filteredQrCodes, setFilteredQrCodes] = useState([])
  const [error, setError] = useState(null)
  const [openQR, setOpenQR] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [totalRedirects, setTotalRedirects] = useState(0)
  const [initializing, setInitializing] = useState(true)

  // Estados para ordenamiento
  const [order, setOrder] = useState("desc")
  const [orderBy, setOrderBy] = useState("numericId")

  // Estados para paginación
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)

  // Estados para GTIN
  const [includeGTIN, setIncludeGTIN] = useState(false)
  const [gtin, setGtin] = useState("")

  // Nuevos estados para logo
  const [includeLogo, setIncludeLogo] = useState(false)
  const [selectedLogo, setSelectedLogo] = useState("logo_bermuda.png")

  // Nuevos estados para MagicBox
  const [isMagicBox, setIsMagicBox] = useState(false)
  const [magicBoxCode, setMagicBoxCode] = useState("")
  const [campaignId, setCampaignId] = useState("")
  const [pedido, setPedido] = useState("")
  const [brand, setBrand] = useState("")
  const [model, setModel] = useState("")
  const [selectedVolumeMl, setSelectedVolumeMl] = useState("")
  const [loading, setLoading] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [snackbarSeverity, setSnackbarSeverity] = useState("success")

  const resolvedMagicBoxSize = useMemo(
    () => resolveVolumeSize(model, selectedVolumeMl),
    [model, selectedVolumeMl],
  )

  // Verificar si la API key está configurada
  useEffect(() => {
    // Verificar si la API key está configurada correctamente
    if (!process.env.REACT_APP_MAGICBOX_API_KEY) {
      console.error('La API key de MagicBox no está configurada en las variables de entorno.');
      console.error('Asegúrate de tener un archivo .env en la raíz del proyecto con:');
      console.error('REACT_APP_MAGICBOX_API_KEY=tu-api-key-aquí');
    } else {
      console.log('API key de MagicBox configurada correctamente.');
    }
    
    // Verificar la URL base de la API
    if (!process.env.REACT_APP_API_BASE_URL) {
      console.warn('La URL base de la API no está configurada. Usando valor predeterminado.');
    } else {
      console.log('URL base de la API configurada:', process.env.REACT_APP_API_BASE_URL);
    }
  }, []);

  // Verificar si el contador ya está inicializado
  useEffect(() => {
    const checkInitialization = async () => {
      try {
        const initialized = await isCounterInitialized()

        if (!initialized) {
          // Solo inicializar si es necesario
          await initializeCounterAndAssignIds()
        }

        setInitializing(false)
      } catch (error) {
        console.error("Error checking initialization:", error)
        setError("Error checking initialization: " + error.message)
        setInitializing(false)
      }
    }

    checkInitialization()
  }, [])

  useEffect(() => {
    const fetchQrCodes = async () => {
      try {
        // Crear una referencia a la colección
        const qrCodesRef = firebase.firestore().collection("qrcodes")

        // Configurar un listener para actualizaciones en tiempo real
        const unsubscribe = qrCodesRef.onSnapshot(
          (snapshot) => {
            const codes = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
              redirects: doc.data().redirects || 0,
              title: doc.data().title || "Untitled",
              gtin: doc.data().gtin || "",
              numericId: doc.data().numericId || 0,
              logo: doc.data().logo || "", // Añadimos soporte para el campo logo
            }))

            // Calcular el total de redirecciones
            const total = codes.reduce((sum, code) => sum + (code.redirects || 0), 0)

            setQrCodes(codes)
            // Inicialmente, mostrar todos los códigos QR
            setFilteredQrCodes(codes)
            setTotalRedirects(total)
          },
          (error) => {
            console.error("Error fetching documents: ", error)
            setError(error.message)
          },
        )

        // Limpiar el listener cuando el componente se desmonte
        return () => unsubscribe()
      } catch (error) {
        console.error("Error setting up listener: ", error)
        setError(error.message)
      }
    }

    if (!initializing) {
      fetchQrCodes()
    }
  }, [initializing]) // Se ejecuta cuando initializing cambia a false

  // Efecto separado para el filtrado y ordenamiento
  useEffect(() => {
    let filtered = [...qrCodes]

    // Aplicar filtro de búsqueda
    if (searchTerm.trim() !== "") {
      const searchTermLower = searchTerm.toLowerCase()

      filtered = filtered.filter((qr) => {
        const titleMatch = qr.title && qr.title.toLowerCase().includes(searchTermLower)
        const urlMatch = qr.url && qr.url.toLowerCase().includes(searchTermLower)
        const gtinMatch = qr.gtin && qr.gtin.toLowerCase().includes(searchTermLower)
        const idMatch = qr.numericId && qr.numericId.toString().includes(searchTermLower)
        const codeMatch = qr.id && qr.id.toLowerCase().includes(searchTermLower)

        return titleMatch || urlMatch || gtinMatch || idMatch || codeMatch
      })
    }

    // Aplicar ordenamiento
    filtered.sort((a, b) => {
      let aValue = a[orderBy]
      let bValue = b[orderBy]

      // Manejar valores nulos o indefinidos
      if (aValue === undefined || aValue === null) aValue = ""
      if (bValue === undefined || bValue === null) bValue = ""

      // Comparar según el tipo de dato
      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue
      } else {
        // Convertir a string para comparación de texto
        aValue = String(aValue).toLowerCase()
        bValue = String(bValue).toLowerCase()
        return order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
      }
    })

    setFilteredQrCodes(filtered)
    // Resetear la página a 0 cuando cambia la búsqueda o el ordenamiento
    setPage(0)
  }, [searchTerm, qrCodes, order, orderBy])

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc"
    setOrder(isAsc ? "desc" : "asc")
    setOrderBy(property)
  }

  const handleGenerateQR = async () => {
    if (isMagicBox) {
      await handleMagicBoxCodeUpload()
    } else {
      if (!url) {
        setError("La URL es requerida")
        return
      }

      try {
        // Obtener el siguiente ID numérico
        const nextId = await getNextId()

        await firebase
          .firestore()
          .collection("qrcodes")
          .add({
            url,
            redirects: 0,
            title: title || "Sin título",
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            gtin: includeGTIN ? gtin : "",
            numericId: nextId,
            logo: includeLogo ? selectedLogo : "", // Guardar el logo seleccionado si se incluye
          })

        // No necesitamos actualizar el estado manualmente aquí
        // El listener onSnapshot se encargará de actualizar el estado

        setUrl("")
        setTitle("")
        setGtin("")
        setIncludeGTIN(false)
        setIncludeLogo(false) // Resetear la opción de logo
        setSelectedLogo("logo_bermuda.png") // Resetear a la opción predeterminada
        setError(null)
      } catch (error) {
        console.error("Error adding document: ", error)
        setError(error.message)
      }
    }
  }

  // Nueva función para manejar la carga de códigos MagicBox
  const handleMagicBoxCodeUpload = async () => {
    if (!magicBoxCode || !campaignId || !pedido || !brand || !model) {
      setError("Todos los campos son requeridos para códigos MagicBox")
      return
    }

    if (!isValidForCodesImport(resolvedMagicBoxSize)) {
      setError("No se pudo determinar el tamaño. Seleccioná uno o incluí el volumen en el modelo (ej. 500ml).")
      return
    }

    if (!MAGICBOX_API_KEY) {
      setError("La API key de MagicBox no está configurada. Contacte al administrador.")
      return
    }

    if (isNaN(parseInt(campaignId)) || isNaN(parseInt(pedido))) {
      setError("Los campos numéricos deben contener valores válidos")
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = {
        code: magicBoxCode,
        campaignId: parseInt(campaignId),
        pedido: parseInt(pedido),
        brand,
        model,
        sizeId: resolvedMagicBoxSize.sizeId,
        pointsToGive: resolvedMagicBoxSize.pointsToGive,
      }

      // Realizar la petición a la API
      const response = await fetch(`${API_BASE_URL}/codes/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-key': MAGICBOX_API_KEY
        },
        body: JSON.stringify(data)
      })

      const result = await response.json()

      if (!response.ok) {
        // Manejar diferentes tipos de errores según el código de estado
        if (response.status === 400) {
          throw new Error(result.message || 'El código ya existe o es inválido')
        } else if (response.status === 401) {
          throw new Error('API key inválida o faltante')
        } else {
          throw new Error(result.message || 'Error al cargar el código')
        }
      }

      // Si la respuesta es exitosa, crear el QR code en Firebase
      const nextId = await getNextId()
      
      await firebase
        .firestore()
        .collection("qrcodes")
        .add({
          url: `${BASE_URL}/magicbox/${magicBoxCode}`,
          redirects: 0,
          title: `MagicBox: ${brand} ${model}`,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          gtin: "",
          numericId: nextId,
          logo: includeLogo ? selectedLogo : "",
          isMagicBox: true,
          magicBoxDetails: {
            code: magicBoxCode,
            campaignId: parseInt(campaignId),
            pedido: parseInt(pedido),
            brand,
            model,
            sizeId: resolvedMagicBoxSize.sizeId,
            pointsToGive: resolvedMagicBoxSize.pointsToGive,
          }
        })

      // Mostrar mensaje de éxito
      setSnackbarMessage("Código MagicBox cargado exitosamente")
      setSnackbarSeverity("success")
      setSnackbarOpen(true)

      // Resetear los campos
      resetMagicBoxFields()
    } catch (error) {
      console.error("Error uploading MagicBox code:", error)
      setError(error.message)
      setSnackbarMessage(`Error: ${error.message}`)
      setSnackbarSeverity("error")
      setSnackbarOpen(true)
    } finally {
      setLoading(false)
    }
  }

  // Función para resetear los campos de MagicBox
  const resetMagicBoxFields = () => {
    setMagicBoxCode("")
    setCampaignId("")
    setPedido("")
    setBrand("")
    setModel("")
    setSelectedVolumeMl("")
    setIsMagicBox(false)
    setIncludeLogo(false)
    setSelectedLogo("logo_bermuda.png")
    setError(null)
  }

  // Función para cerrar el Snackbar
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  const handleEditQR = async (id, newUrl, newTitle, newGtin, newLogo) => {
    try {
      const updateData = {
        url: newUrl,
        title: newTitle,
      }

      // Solo actualizamos el GTIN si se proporciona
      if (newGtin !== undefined) {
        updateData.gtin = newGtin
      }

      // Solo actualizamos el logo si se proporciona
      if (newLogo !== undefined) {
        updateData.logo = newLogo
      }

      await firebase.firestore().collection("qrcodes").doc(id).update(updateData)

      // No necesitamos actualizar el estado manualmente aquí
      // El listener onSnapshot se encargará de actualizar el estado

      setError(null)
    } catch (error) {
      console.error("Error updating document: ", error)
      setError(error.message)
    }
  }

  const handleDeleteQR = async (id) => {
    if (!window.confirm("Are you sure you want to delete this QR code?")) {
      return
    }

    try {
      await firebase.firestore().collection("qrcodes").doc(id).delete()

      // No necesitamos actualizar el estado manualmente aquí
      // El listener onSnapshot se encargará de actualizar el estado

      setError(null)
    } catch (error) {
      console.error("Error deleting document: ", error)
      setError(error.message)
    }
  }

  // Función mejorada para descargar QR Code
  const downloadQRCode = (qrId, title, gtin = "", logo = "") => {
    // Crear un ID único para el elemento
    const qrId2 = `qr-${qrId}-download`

    // Crear un contenedor temporal
    const tempContainer = document.createElement("div")
    tempContainer.style.position = "absolute"
    tempContainer.style.left = "-9999px"
    document.body.appendChild(tempContainer)

    // Construir la URL del QR
    const qrUrl = gtin ? `${BASE_URL}/${qrId}/${gtin}` : `${BASE_URL}/${qrId}`

    // Renderizar el QR code en el contenedor temporal
    const ReactDOM = require("react-dom")

    if (logo) {
      // Si tiene logo, usar qrcode.react
      ReactDOM.render(
        <QRCode
          id={qrId2}
          value={qrUrl}
          size={256}
          level="H" // Alto nivel de corrección de errores para el logo
          imageSettings={{
            src: `/logos/${logo}`,
            height: 60,
            width: 60,
            excavate: true,
          }}
          renderAs="svg"
        />,
        tempContainer,
      )
    } else {
      // Si no tiene logo, usar react-qr-code para mantener compatibilidad
      ReactDOM.render(<ReactQRCode id={qrId2} value={qrUrl} size={256} />, tempContainer)
    }

    // Obtener el elemento SVG
    const svgElement = document.getElementById(qrId2)

    if (!svgElement) {
      console.error("Could not create or find SVG element")
      document.body.removeChild(tempContainer)
      return
    }

    // Convertir el SVG a una cadena
    const svgData = new XMLSerializer().serializeToString(svgElement)
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" })
    const svgUrl = URL.createObjectURL(svgBlob)

    // Crear y hacer clic en el enlace de descarga
    const downloadLink = document.createElement("a")
    downloadLink.href = svgUrl
    downloadLink.download = `qr-${title.replace(/\s+/g, "-").toLowerCase()}.svg`
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)

    // Limpiar
    URL.revokeObjectURL(svgUrl)
    document.body.removeChild(tempContainer)
  }

  const handleEditClick = (qr) => {
    const newUrl = prompt("Enter new URL", qr.url)
    const newTitle = prompt("Enter new title", qr.title)

    // Si el QR tiene GTIN, permitir editarlo
    let newGtin = undefined
    if (qr.gtin !== undefined) {
      newGtin = prompt("Enter new GTIN (leave empty to remove)", qr.gtin || "")
    }

    // Si el QR tiene logo, permitir editarlo
    let newLogo = undefined
    if (qr.logo !== undefined) {
      const includeLogo = window.confirm("Include logo in QR code?")
      if (includeLogo) {
        const logoOptions = LOGO_OPTIONS.map((option) => `${option.value} - ${option.label}`).join("\n")
        const logoInput = prompt(
          `Select logo (enter one of the following):\n${logoOptions}`,
          qr.logo || "logo_bermuda.png",
        )
        newLogo = logoInput && LOGO_OPTIONS.some((option) => option.value === logoInput) ? logoInput : ""
      } else {
        newLogo = ""
      }
    }

    if (newUrl && newTitle) {
      handleEditQR(qr.id, newUrl, newTitle, newGtin, newLogo)
    }
  }

  // Funciones para manejar la paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  // Función para truncar texto
  const truncateText = (text, maxLength = 30) => {
    if (!text) return ""
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + "..."
  }

  // Función para limpiar la búsqueda
  const clearSearch = () => {
    setSearchTerm("")
  }

  // Función para renderizar el QR code correcto según si tiene logo o no
  const renderQRCode = (qrId, qrUrl, logo) => {
    if (logo) {
      return (
        <QRCode
          id={`qr-${qrId}`}
          value={qrUrl}
          size={256}
          level="H"
          imageSettings={{
            src: `/logos/${logo}`,
            height: 60,
            width: 60,
            excavate: true,
          }}
          renderAs="svg"
        />
      )
    } else {
      return <ReactQRCode id={`qr-${qrId}`} value={qrUrl} size={256} />
    }
  }

  // Obtener los elementos para la página actual
  const paginatedQrCodes = filteredQrCodes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  // Si estamos inicializando, mostrar un spinner
  if (initializing) {
    return (
      <>
        <Navbar />
        <Container
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh",
          }}
        >
          <CircularProgress size={60} style={{ color: "#6366f1" }} />
          <Typography variant="h6" style={{ marginTop: "1.5rem", color: "#1f2937" }}>
            Initializing QR Codes...
          </Typography>
          <Typography variant="body2" style={{ marginTop: "0.5rem", color: "#6b7280" }}>
            This may take a moment if you have many QR codes
          </Typography>
        </Container>
      </>
    )
  }

  return (
    <>
      <Navbar />
      <Container maxWidth="lg" className="dashboard">
        <Typography variant="h4" gutterBottom className="dashboard-title">
          QR Code Dashboard
        </Typography>

        <div className="stats-container">
          <Paper className="stats-paper">
            <Typography variant="h6">Total QR Codes</Typography>
            <Typography variant="h3">{qrCodes.length}</Typography>
          </Paper>
          <Paper className="stats-paper">
            <Typography variant="h6">Total Redirects</Typography>
            <Typography variant="h3">{totalRedirects}</Typography>
          </Paper>
        </div>

        {error && (
          <Typography color="error" gutterBottom>
            Error: {error}
          </Typography>
        )}

        <div className="qr-generator">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={isMagicBox} 
                    onChange={(e) => {
                      setIsMagicBox(e.target.checked)
                      if (e.target.checked) {
                        setUrl("")
                        setIncludeGTIN(false)
                      }
                    }} 
                    color="primary" 
                  />
                }
                label="Código MagicBox"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {!isMagicBox && (
                <FormControlLabel
                  control={
                    <Checkbox checked={includeGTIN} onChange={(e) => setIncludeGTIN(e.target.checked)} color="primary" />
                  }
                  label="Incluir GTIN"
                />
              )}
            </Grid>

            {isMagicBox ? (
              // Campos para MagicBox
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Código"
                    fullWidth
                    margin="normal"
                    value={magicBoxCode}
                    onChange={(e) => setMagicBoxCode(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="ID de Campaña"
                    fullWidth
                    margin="normal"
                    value={campaignId}
                    onChange={(e) => setCampaignId(e.target.value)}
                    type="number"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Pedido"
                    fullWidth
                    margin="normal"
                    value={pedido}
                    onChange={(e) => setPedido(e.target.value)}
                    type="number"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Marca"
                    fullWidth
                    margin="normal"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Modelo"
                    fullWidth
                    margin="normal"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Tamaño</InputLabel>
                    <Select
                      value={selectedVolumeMl}
                      onChange={(e) => setSelectedVolumeMl(e.target.value)}
                    >
                      <MenuItem value="">Auto (detectar del modelo)</MenuItem>
                      {VOLUME_SIZE_OPTIONS.map((option) => (
                        <MenuItem key={option.ml} value={String(option.ml)}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Puntos a otorgar"
                    fullWidth
                    margin="normal"
                    value={resolvedMagicBoxSize.pointsToGive ?? ""}
                    InputProps={{ readOnly: true }}
                    helperText={
                      resolvedMagicBoxSize.source === "manual"
                        ? "Según tamaño seleccionado"
                        : resolvedMagicBoxSize.source === "parser"
                          ? "Detectado del modelo"
                          : "Seleccioná tamaño o incluí volumen en el modelo"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox checked={includeLogo} onChange={(e) => setIncludeLogo(e.target.checked)} color="primary" />
                    }
                    label="Incluir Logo"
                  />
                </Grid>
              </>
            ) : (
              // Campos para QR normal
              <>
                <Grid item xs={12}>
                  <TextField
                    label="Título/Descripción"
                    fullWidth
                    margin="normal"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="title-input"
                    placeholder="Ingrese un título o descripción para este código QR"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="URL"
                    fullWidth
                    margin="normal"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    className="url-input"
                    placeholder="https://ejemplo.com"
                    required
                  />
                </Grid>
                {includeGTIN && (
                  <Grid item xs={12}>
                    <TextField
                      label="GTIN"
                      fullWidth
                      margin="normal"
                      value={gtin}
                      onChange={(e) => setGtin(e.target.value)}
                      className="gtin-input"
                      placeholder="Ingrese código GTIN"
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox checked={includeLogo} onChange={(e) => setIncludeLogo(e.target.checked)} color="primary" />
                    }
                    label="Incluir Logo"
                  />
                </Grid>
              </>
            )}

            {includeLogo && (
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Select Logo</InputLabel>
                  <Select value={selectedLogo} onChange={(e) => setSelectedLogo(e.target.value)}>
                    {LOGO_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box mt={2} display="flex" justifyContent="center">
                  <img
                    src={`/logos/${selectedLogo}`}
                    alt="Selected Logo"
                    style={{ width: "60px", height: "60px", objectFit: "contain" }}
                  />
                </Box>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateQR}
                className="generate-button"
                disabled={loading || (isMagicBox ?
                  (!magicBoxCode || !campaignId || !pedido || !brand || !model || !isValidForCodesImport(resolvedMagicBoxSize)) :
                  !url)}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : isMagicBox ? "Cargar Código MagicBox" : "Generar Código QR"}
              </Button>
            </Grid>
          </Grid>
        </div>

        <div className="search-container">
          <TextField
            label="Search by ID, code, title, URL or GTIN"
            fullWidth
            margin="normal"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {searchTerm && (
            <Typography variant="body2" style={{ marginTop: "0.5rem", color: "#6b7280" }}>
              Showing {filteredQrCodes.length} of {qrCodes.length} QR codes
            </Typography>
          )}
        </div>

        <TableContainer component={Paper} className="qr-table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "numericId"}
                    direction={orderBy === "numericId" ? order : "asc"}
                    onClick={() => handleRequestSort("numericId")}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "id"}
                    direction={orderBy === "id" ? order : "asc"}
                    onClick={() => handleRequestSort("id")}
                  >
                    Código
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "title"}
                    direction={orderBy === "title" ? order : "asc"}
                    onClick={() => handleRequestSort("title")}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "url"}
                    direction={orderBy === "url" ? order : "asc"}
                    onClick={() => handleRequestSort("url")}
                  >
                    URL
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "gtin"}
                    direction={orderBy === "gtin" ? order : "asc"}
                    onClick={() => handleRequestSort("gtin")}
                  >
                    GTIN
                  </TableSortLabel>
                </TableCell>
                <TableCell>Logo</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === "redirects"}
                    direction={orderBy === "redirects" ? order : "asc"}
                    onClick={() => handleRequestSort("redirects")}
                  >
                    Redirects
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedQrCodes.length > 0 ? (
                paginatedQrCodes.map((qr) => (
                  <TableRow key={qr.id}>
                    <TableCell>{qr.numericId || "-"}</TableCell>
                    <TableCell title={qr.id} style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{qr.id || "-"}</TableCell>
                    <TableCell title={qr.title}>{truncateText(qr.title)}</TableCell>
                    <TableCell title={qr.url}>{truncateText(qr.url)}</TableCell>
                    <TableCell>{qr.gtin || "-"}</TableCell>
                    <TableCell>
                      {qr.logo ? (
                        <img
                          src={`/logos/${qr.logo}`}
                          alt="Logo"
                          style={{ width: "24px", height: "24px", objectFit: "contain" }}
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell align="right">{qr.redirects}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => setOpenQR(qr)} title="View QR Code">
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton onClick={() => handleEditClick(qr)} title="Edit">
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => downloadQRCode(qr.id, qr.title, qr.gtin, qr.logo)}
                        title="Download SVG"
                      >
                        <DownloadIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteQR(qr.id)} title="Delete">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {searchTerm ? (
                      <Typography variant="body1" style={{ padding: "2rem 0" }}>
                        No QR codes found matching "{searchTerm}".{" "}
                        <Button color="primary" onClick={clearSearch}>
                          Clear search
                        </Button>
                      </Typography>
                    ) : (
                      <Typography variant="body1" style={{ padding: "2rem 0" }}>
                        No QR codes found. Create your first QR code above.
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {filteredQrCodes.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredQrCodes.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          )}
        </TableContainer>

        <Dialog open={!!openQR} onClose={() => setOpenQR(null)}>
          <DialogTitle>{openQR?.numericId ? `#${openQR.numericId} - ${openQR.title}` : openQR?.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>Scan this QR code to be redirected to: {openQR?.url}</DialogContentText>
            {openQR?.gtin && <DialogContentText>GTIN: {openQR.gtin}</DialogContentText>}
            {openQR?.logo && <DialogContentText>Logo: {openQR.logo}</DialogContentText>}
            {openQR && (
              <div className="qr-code-container">
                {renderQRCode(
                  openQR.id,
                  openQR.gtin ? `${BASE_URL}/${openQR.id}/${openQR.gtin}` : `${BASE_URL}/${openQR.id}`,
                  openQR.logo,
                )}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={() => downloadQRCode(openQR.id, openQR.title, openQR.gtin, openQR.logo)}
                  className="download-button"
                  style={{ marginTop: "1rem" }}
                >
                  Download SVG
                </Button>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenQR(null)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Snackbar for notifications */}
        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
          <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  )
}

export default Dashboard
